/**
 * Client-side driver for the asynchronous Excel export contract.
 *
 * Every `api/v1/ExcelGenerator/Generate*` endpoint queues work and returns a job id immediately;
 * the file is generated server-side, written to blob storage, and handed back as a time-limited
 * SAS download URL once `GetExcelExportStatus` reports `Ready`. This module owns the poll loop
 * and the terminal-state interpretation so callers only deal with a finished job.
 *
 * Deliberately free of any API imports: the caller supplies `start` and `pollStatus`, which keeps
 * this file type-safe without depending on the untyped `.js` api layer.
 */

export type ExcelExportStatus =
	| "Queued" // accepted, waiting for a worker
	| "Generating" // being built
	| "Uploading" // built, being written to blob storage
	| "Ready" // downloadUrl is populated
	| "Failed" // generation errored — offer a retry
	| "Abandoned" // worker was lost (deploy/restart) — offer a retry
	| "NoData"; // nothing matched the request — not an error

export interface ExcelExportJob {
	jobId: string;
	status: ExcelExportStatus;
	/** Display text for the current phase, safe to show as-is. */
	statusDescription: string;
	/** True once `status` will not change again. Preferred over enumerating terminal statuses. */
	isFinished: boolean;
	fileName: string | null;
	fileSizeInBytes: number;
	/** Populated only when `status === "Ready"`. */
	downloadUrl: string | null;
	downloadUrlExpiresOnUtc: string | null;
	/** User-safe text on Failed/Abandoned. */
	errorMessage: string | null;
	queuedOnUtc: string;
	completedOnUtc: string | null;
	suggestedRetryAfterSeconds: number;
}

/**
 * The API-wide response envelope. The HTTP transport status is always 200 — the real status lives
 * in `statusCode`, so every branch below reads the body rather than `Response.status`.
 * On 401/404 `data` is a plain string message rather than an object.
 */
export interface ApiEnvelope<T> {
	statusCode: number;
	statusDescription: string;
	messages: string[];
	data: T;
}

export type StartExcelExport = () => Promise<ApiEnvelope<unknown>>;
export type PollExcelExportStatus = (jobId: string, waitSeconds: number) => Promise<ApiEnvelope<unknown>>;

/**
 * Always ask the server to answer immediately rather than holding the connection.
 *
 * The endpoint supports a server-side wait (`waitSeconds`, capped at 25), which would make short
 * exports feel synchronous. We deliberately do not use it: `aerialStatisticsApi.baseUrl` also serves
 * every heatmap and analysis pull in the app (see `analysisApi.js`, `curveModelsApi.js`,
 * `statisticsApi.js`), and the export modal is mounted from the secondary left nav on exactly the
 * pages that fan out hardest to that host. A held request parks a request-processing slot there for
 * its whole duration, queueing the calls the page needs to render — which reads to the user as the
 * page locking up until the wait elapses. Short polls cost more round trips but block nothing.
 */
const SERVER_WAIT_SECONDS = 0;

/** Opening cadence, and the floor for the payload's own hint. */
const INITIAL_POLL_SECONDS = 2;

/** Ceiling on the gap, so a 15-minute export costs ~60-70 cheap requests rather than one every 2s. */
const MAX_POLL_SECONDS = 15;

const BACKOFF_FACTOR = 1.5;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * How long to wait before the next poll, seeded from the payload's hint.
 *
 * The clamp matters: `suggestedRetryAfterSeconds` is `0` on terminal statuses (and could be absent
 * against an older backend), so trusting it directly would spin a hot loop.
 */
const pollGapSeconds = (job: ExcelExportJob) =>
	Math.min(Math.max(job.suggestedRetryAfterSeconds || INITIAL_POLL_SECONDS, INITIAL_POLL_SECONDS), MAX_POLL_SECONDS);

/** Pull the job out of an envelope, converting non-200 `statusCode` values into thrown errors. */
function unwrapJob(envelope: ApiEnvelope<unknown>): ExcelExportJob {
	if (!envelope || typeof envelope.statusCode !== "number") {
		throw new Error("The export service returned an unexpected response.");
	}

	if (envelope.statusCode === 404) {
		// A job id that does not exist, or belongs to another user or client — deliberately
		// indistinguishable so job ids cannot be probed.
		throw new Error("This export is no longer available. Please start a new one.");
	}

	if (envelope.statusCode !== 200) {
		const message = typeof envelope.data === "string" ? envelope.data : "";
		throw new Error(message || envelope.statusDescription || "The export could not be started.");
	}

	return envelope.data as ExcelExportJob;
}

/**
 * Queue an export and poll until it reaches a terminal state.
 *
 * Polls are short (`SERVER_WAIT_SECONDS`) with a widening gap: 2s, 3s, 4.5s … capped at 15s, reset
 * on every phase change. See `SERVER_WAIT_SECONDS` for why we don't let the server hold the request.
 *
 * The backend coalesces duplicate in-flight requests for the same user, so `start` may hand back a
 * job that is already `Generating` or even `Ready` rather than `Queued`. Nothing here assumes an
 * initial status: the loop tests `isFinished` before its first sleep, so a reused finished job skips
 * polling entirely.
 *
 * @param start - Fires the `Generate*` endpoint. Resolves to the queued job envelope.
 * @param pollStatus - Calls `GetExcelExportStatus` for a job id. The second argument is the endpoint's
 *   server-side wait, which this loop always passes as 0.
 * @returns The finished job. Callers must still branch on `status` — a resolved promise means the
 *   job reached a terminal state, not that a file exists (see `NoData`, `Failed`, `Abandoned`).
 * @throws If the export could not be queued or tracked. The message is user-safe.
 */
export async function runExcelExport(
	start: StartExcelExport,
	pollStatus: PollExcelExportStatus,
): Promise<ExcelExportJob> {
	let job = unwrapJob(await start());

	let gapSeconds = pollGapSeconds(job);

	while (!job.isFinished) {
		// Sleep first: the response in hand is already current, so polling immediately would just
		// re-ask for what we know.
		await delay(gapSeconds * 1000);

		const previousStatus = job.status;
		job = unwrapJob(await pollStatus(job.jobId, SERVER_WAIT_SECONDS));

		// Widen the gap while nothing is changing, but snap back on a phase transition so
		// Queued → Generating → Uploading still reaches the user promptly.
		gapSeconds =
			job.status === previousStatus ? Math.min(gapSeconds * BACKOFF_FACTOR, MAX_POLL_SECONDS) : pollGapSeconds(job);
	}

	return job;
}

/** User-facing message for a job that finished without producing a file. */
export function exportFailureMessage(job: ExcelExportJob): string {
	if (job.status === "Abandoned") {
		// A deploy or restart interrupted the worker; a retry is very likely to succeed, so say that
		// rather than surfacing this as a generic failure.
		return "The export was interrupted. Please try again.";
	}

	return job.errorMessage ?? "The export did not complete. Please try again.";
}

/**
 * Start the browser download for a `Ready` job.
 *
 * The SAS URL already carries `Content-Disposition: attachment` and the correct `Content-Type`, so
 * the file saves with the backend-assigned name and the page does not navigate. Note this is a
 * plain navigation, not a `fetch` — reading the bytes in JS would require CORS rules on the storage
 * account, which are not configured. No `download` attribute: it is ignored cross-origin anyway.
 */
export function triggerExcelDownload(url: string): void {
	const link = document.createElement("a");
	link.href = url;
	link.rel = "noopener";

	document.body.appendChild(link); // Required for this to work in Firefox
	link.click();
	document.body.removeChild(link);
}
